// Telemetry adapter. Default = REAL data via SSE (EventSource) from the Node API
// (/scada/overview/stream), refreshed at the stream tick (~15 s, bounded by the
// Modbus collector cadence ~5 min). Falls back to the mock generator when
// VITE_SCADA_PREVIEW='true' is set, or automatically if the stream errors so the
// UI never blanks. Components consume StationTelemetry[] unchanged.
import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '@/services/api';
import { STATIONS } from './mock/stations';
import { snapshotFleet, snapshotStation, type StationTelemetry, type LimitReason, type ControlMode, type CommandStatus } from './mock/telemetry';
import type { OpState, Quality } from './tokens';

const FORCE_MOCK = (import.meta.env.VITE_SCADA_PREVIEW ?? 'false') === 'true';
const STREAM_URL = `${API_BASE_URL}/scada/overview/stream`;
const STALE_SEC = 40 * 60;          // older than 40 min → treat as stale (safe for 30-min collector cadence)
const MOCK_REFRESH_MS = 5000;

// ─── Backend payload types ───────────────────────────────────────────────────
interface ApiBess {
  powerKw: number | null; dcPowerKw: number | null; flow: string | null;
  status: string | null; bmsStatus: string | null;
  socPct: number | null; sohPct: number | null;
  batteryV: number | null; batteryA: number | null;
  innerTempC: number | null; gridHz: number | null;
  dailyChargeKwh: number | null; dailyDischargeKwh: number | null;
  chargeAvailableKwh: number | null; dischargeAvailableKwh: number | null;
  qualityFlag: string | null;
}
interface ApiStation {
  assetId: string; eicW: string | null; name: string; capacityKw: number | null;
  curtailmentPct: number | null; operationalStatus: string;
  readAtLocal: string | null; ageSec: number | null;
  pacKw: number | null; ppvKw: number | null;
  availableInverters: number | null; expectedInverters: number | null;
  qualityFlag: string | null; dailyKwh: number | null;
  // Наявність УЗЕ за довідником бекенду (asset_bess_systems) — окремо від
  // живих показань. Поле є у відповіді /scada/overview і його вже читає
  // портфельний дашборд; SCADA-адаптер його ІГНОРУВАВ, через що модуль
  // підмінював дані захардкодженим списком у mock/stations.ts.
  hasBess?: boolean;
  bess: ApiBess | null;
}
interface ApiPayload { generatedAt: string; tickMs: number; stations: ApiStation[]; }

const EIC_TO_ID: Record<string, string> = Object.fromEntries(
  STATIONS.filter((s) => s.eicW).map((s) => [s.eicW as string, s.id]),
);

// ─── Map one backend row → StationTelemetry ──────────────────────────────────
function mapRow(r: ApiStation): StationTelemetry | null {
  const id = r.eicW ? EIC_TO_ID[r.eicW] : undefined;
  if (!id) return null;
  const cfg = STATIONS.find((s) => s.id === id)!;
  const capKw = r.capacityKw ?? cfg.acCapacityKw;

  const hasData = r.readAtLocal != null && r.ageSec != null;
  const stale = !hasData || (r.ageSec ?? 1e9) > STALE_SEC;
  const expected = r.expectedInverters ?? cfg.inverterCount;
  const working = r.availableInverters ?? 0;
  const pac = r.pacKw ?? 0;

  // No reachable inverters at all + no power → station effectively offline/no-link.
  const noLink = !hasData || (working === 0 && r.pacKw == null);

  const curtailed = r.curtailmentPct != null && r.curtailmentPct > 0 && r.curtailmentPct < 100;
  const activeLimitKw = curtailed ? Math.round(capKw * ((r.curtailmentPct as number) / 100)) : null;

  // Stale telemetry (collector/logger silent) MUST override "ok": a frozen last
  // reading (e.g. Табакі stuck at 725 kW for 2h while ShineMaster is down) must
  // NOT show as «Норма». Treat stale-or-no-link as «Немає звʼязку».
  const linkLost = noLink || stale;
  let state: OpState;
  if (cfg.ownership === 'commissioning' && linkLost) state = 'commissioning';
  else if (linkLost) state = 'offline';
  else if (working < expected) state = 'warning';
  else if (curtailed) state = 'curtailed';
  else state = 'ok';

  const quality: Quality = linkLost ? 'stale' : 'good';
  const limitReason: LimitReason = curtailed ? 'technical' : null;
  const controlMode: ControlMode = linkLost ? 'blocked' : 'remote';
  const commandStatus: CommandStatus = 'idle';

  let bess;
  if (r.bess) {
    const out = r.bess.powerKw ?? 0;
    const mode: 'idle' | 'charging' | 'discharging' | 'fault' =
      r.bess.status === 'fault' ? 'fault'
      : Math.abs(out) < 0.2 ? 'idle'
      : out < 0 ? 'charging' : 'discharging';
    bess = {
      socPct: r.bess.socPct ?? 0,
      powerKw: out,
      mode,
      chargeAvailable: (r.bess.chargeAvailableKwh ?? 0) > 0,
      dischargeAvailable: (r.bess.dischargeAvailableKwh ?? 0) > 0,
      sohPct: r.bess.sohPct,
      dcPowerKw: r.bess.dcPowerKw,
      batteryV: r.bess.batteryV,
      batteryA: r.bess.batteryA,
      innerTempC: r.bess.innerTempC,
      gridHz: r.bess.gridHz,
      dailyChargeKwh: r.bess.dailyChargeKwh,
      dailyDischargeKwh: r.bess.dailyDischargeKwh,
      chargeAvailableKwh: r.bess.chargeAvailableKwh,
      dischargeAvailableKwh: r.bess.dischargeAvailableKwh,
      bmsStatus: r.bess.bmsStatus,
      qualityFlag: r.bess.qualityFlag,
    };
  }

  return {
    stationId: id,
    assetId: r.assetId,
    eicW: r.eicW,
    hasBess: !!r.hasBess || !!r.bess,
    state,
    quality,
    activePowerKw: Math.round(pac),
    dailyEnergyKwh: Math.round(r.dailyKwh ?? 0),
    // Запас потужності без прогнозу невідомий, тож поле дорівнює поточній
    // генерації. НЕ показувати його як окрему метрику — це дубль гейджа.
    availablePowerKw: Math.round(pac),
    dcPowerKw: r.ppvKw != null ? Math.round(r.ppvKw) : null,
    activeLimitKw,
    limitReason,
    controlMode,
    commandStatus,
    invertersWorking: working,
    inverterCount: expected,
    pf: 0,                                     // not in realtime payload → UI shows «—»
    freqHz: 0,
    performanceRatio: 0,
    alarms: 0,
    warnings: working < expected ? 1 : 0,
    bess,
    comms: {
      cloudOnline: true,
      emsBoxOnline: hasData && !stale,
      vpnOnline: true,
      lastPacketSec: r.ageSec ?? 0,
      bufferState: 'empty',
      bufferHours: 7,
    },
    updatedAt: r.readAtLocal ?? new Date().toISOString(),
  };
}

function mapPayload(p: ApiPayload): StationTelemetry[] {
  // Preserve STATIONS order; fill from backend rows; fall back per-station to mock
  // only for stations with no backend row at all (keeps UI complete).
  const byId = new Map<string, StationTelemetry>();
  for (const row of p.stations) {
    const m = mapRow(row);
    if (m) byId.set(m.stationId, m);
  }
  return STATIONS.map((s) => byId.get(s.id) ?? snapshotStation(s.id)!);
}

// ─── Shared SSE subscription (one connection for the whole app) ──────────────
let es: EventSource | null = null;
let latest: StationTelemetry[] | null = null;
let lastAt = new Date();
const subs = new Set<() => void>();

function ensureStream() {
  if (FORCE_MOCK || es) return;
  try {
    es = new EventSource(STREAM_URL);
    es.addEventListener('snapshot', (ev) => {
      try {
        const payload = JSON.parse((ev as MessageEvent).data) as ApiPayload;
        latest = mapPayload(payload);
        lastAt = new Date();
        subs.forEach((fn) => fn());
      } catch { /* ignore parse errors */ }
    });
    es.onerror = () => { /* EventSource auto-reconnects; keep last snapshot */ };
  } catch {
    es = null;
  }
}

export function useFleetTelemetry(): { data: StationTelemetry[]; updatedAt: Date } {
  const [, force] = useState(0);
  const [mock, setMock] = useState<StationTelemetry[] | null>(() => (FORCE_MOCK ? snapshotFleet() : null));

  useEffect(() => {
    if (FORCE_MOCK) {
      const t = setInterval(() => setMock(snapshotFleet()), MOCK_REFRESH_MS);
      return () => clearInterval(t);
    }
    ensureStream();
    const onUpdate = () => force((n) => n + 1);
    subs.add(onUpdate);
    return () => { subs.delete(onUpdate); };
  }, []);

  if (FORCE_MOCK) return { data: mock ?? snapshotFleet(), updatedAt: new Date() };
  // Until first SSE snapshot arrives, show a mock frame so the UI is never empty.
  return { data: latest ?? snapshotFleet(), updatedAt: lastAt };
}

export function useStationTelemetry(id: string): { data: StationTelemetry | undefined; updatedAt: Date } {
  const fleet = useFleetTelemetry();
  return { data: fleet.data.find((d) => d.stationId === id), updatedAt: fleet.updatedAt };
}

export const SCADA_PREVIEW_MODE = FORCE_MOCK;
